import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigation } from '../hooks/useLocalizedNavigation';
import { Facebook, Instagram, Linkedin, Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedNavigation();

  return (
    <footer className="bg-white border-t border-gray-200 pt-10 pb-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Contact Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10 border-b border-gray-200 pb-10">
          <div className="text-center md:text-left">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-start">
              <MapPin className="h-5 w-5 mr-2" />
              {t('footer.location')}
            </h4>
            <p
              className="text-sm text-gray-600"
              dangerouslySetInnerHTML={{ __html: t('footer.address') }}
            />
          </div>
          <div className="text-center">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center justify-center">
              <Phone className="h-5 w-5 mr-2" />
              {t('footer.contactUs')}
            </h4>
            <p className="text-sm text-gray-600">
              {t('footer.phone')}: <a href="tel:+595972350400" className="hover:underline">+595 972 350400</a><br />
              {t('footer.email')}: <a href="mailto:info@realtypy.com" className="hover:underline">info@realtypy.com</a><br />
              <a
                href="https://wa.me/595972350400?text=Hola,%20como%20podemos%20ayudarte?"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center mt-1 hover:underline text-green-600"
              >
                <MessageCircle className="h-4 w-4 mr-1" />
                WhatsApp
              </a>
            </p>
          </div>
          <div className="text-center md:text-right">
            <h4 className="font-bold text-gray-900 mb-4 flex items-center justify-center md:justify-end">
              <Mail className="h-5 w-5 mr-2" />
              {t('footer.businessHours')}
            </h4>
            <p className="text-sm text-gray-600">
              {t('footer.mondayFriday')}<br />
              {t('footer.saturday')}
            </p>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-8 text-sm text-gray-600">
          <Link to={getLocalizedPath('/acerca-de')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.about')}</Link>
          <Link to={getLocalizedPath('/trabajos')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.careers')}</Link>
          <Link to={getLocalizedPath('/ayuda')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.help')}</Link>
          <Link to={getLocalizedPath('/legal')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.legal')}</Link>
          <Link to={getLocalizedPath('/terminos')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.termsOfUse')}</Link>
          <Link to={getLocalizedPath('/privacidad')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.privacyPortal')}</Link>
          <Link to={getLocalizedPath('/cookies')} onClick={() => window.scrollTo(0, 0)} className="hover:underline">{t('footer.links.cookiePreference')}</Link>
        </div>

        {/* Legal Text */}
        <div className="text-center text-xs text-gray-500 space-y-4 mb-8 max-w-4xl mx-auto">
          <p>
            {t('footer.aboutInfo')}
          </p>
          <p>
            {t('footer.careersInfo')}
          </p>
          <p>
            {t('footer.helpInfo')}
          </p>
          <p>
            {t('common.accessibilityStatement')}
          </p>
          <p>
            {t('common.licenseInfo')}
          </p>
        </div>

        {/* Social Media & Copyright */}
        <div className="flex flex-col items-center space-y-4">
          <div className="flex space-x-6 text-gray-400">
            <a href="https://www.facebook.com/profile.php?id=61587151294547" target="_blank" rel="noopener noreferrer">
              <Facebook className="h-6 w-6 hover:text-gray-600 cursor-pointer" />
            </a>
            <a href="https://www.instagram.com/realtypy/" target="_blank" rel="noopener noreferrer">
              <Instagram className="h-6 w-6 hover:text-gray-600 cursor-pointer" />
            </a>
            {/* <a href="https://twitter.com/realtypy" target="_blank" rel="noopener noreferrer">
              <Twitter className="h-6 w-6 hover:text-gray-600 cursor-pointer" />
            </a> */}
            <a href="https://www.linkedin.com/company/realtypy" target="_blank" rel="noopener noreferrer">
              <Linkedin className="h-6 w-6 hover:text-gray-600 cursor-pointer" />
            </a>
          </div>
          <div className="flex flex-col items-center space-y-2">
            <div className="flex items-center space-x-2">
               <span className="text-2xl font-bold text-blue-600">Realty Paraguay</span>
            </div>
            <span className="text-gray-600 text-sm">{t('footer.copyright')}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
